import { Component, ElementRef, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { Alert } from 'src/app/interfaces/alert';
import {
  ProductType,
  RegistrationPaymentInfo,
  User,
} from 'src/app/interfaces/user';
import { LogService } from 'src/app/services/common/log.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { RegistrationFlowService } from 'src/app/services/registration-flow/registration-flow.service';
import { PaymentConfigService } from 'src/app/services/payment-config/payment-config.service';
import { CheckoutService } from 'src/app/services/checkout/checkout.service';
import { RegistrationFlowStep, STEP_ROUTES } from 'src/app/constants/registration-flow.constants';
import { VET_GROUP_V4P } from 'src/app/constants/payment-config.constants';
import { errorMessagesMap } from 'src/app/constants/error-messages';
import { environment } from 'src/environments/environment';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { PlansPageConfig } from 'src/app/interfaces/pageConfig';
import { IdentialertSubSelectionModalComponent } from 'src/app/shared/components/subscription-pages/identialert-sub-selection-modal/identialert-sub-selection-modal.component';
import { SmartTagLimit, SmartTagOrderItem } from 'src/app/interfaces/smart-tag';
import { SmartTagService } from 'src/app/services/smart-tag/smart-tag.service';
import { PetService } from 'src/app/services/pet/pet.service';

@Component({
  selector: 'app-membership-options-v2',
  templateUrl: './membership-options-v2.component.html',
  styleUrl: './membership-options-v2.component.scss',
})
export class MembershipOptionsV2Component implements OnInit {
  public RegistrationFlowStep = RegistrationFlowStep;
  // Hidden once the step is completed, for example for keepers adding a pet after registration
  public showProgressStepper = false;
  public alert: Alert = null;
  public loading: boolean;
  public user: User;
  public currentMembershipName: string = '';
  public userAlreadyHasASubscription: boolean;
  public petName: string;
  public userCurrentProductType: number;
  public isIdentiSmartTagPlanAvailable: boolean;
  public showBasicAccountButton: boolean = true;
  public showVetGroupMessage: boolean = false;

  private isBrowser: boolean;

  // Registration payment config (/api/registration/me/payment)
  private isRegistrationPaymentConfigEnabled: boolean = false;
  public availableProductIds: number[] | null = null;

  public page: PlansPageConfig;
  public plan1Name: string;
  public plan2Name: string;
  public plan3Name: string;
  public plan1Route: string;
  public plan2Route: string;
  public plan3Route: string;
  public membershipSelectionVet4PetTitle: string;
  public membershipSelectionSubTitle: string;
  public membershipSelectionSubTitleButtonText: string;
  public plan1YearlyPrice: string;
  public plan2YearlyPrice: string;
  public plan2MonthlyPrice: string;
  public plan3YearlyPrice: string;
  public plan3YearlyPriceOriginal: string;
  /** Price of a single paid smart tag, passed to the tag configurator. */
  public tagUnitPrice: number;
  /** The tag picker stays hidden until the keeper chooses the Locator plan. */
  public showSmartTagPicker = false;
  /** How many tags the user has remaining */
  public tagPricing: SmartTagLimit | null = null;
  /** Set when the allowance request failed */
  private limitFailed = false;
  public plan1Benefits: string[];
  public plan2Benefits: string[];
  public plan3Benefits: string[];
  public basePlanSubtitle: string;
  public alertPlanSubtitle: string;
  public locatorPlanSubtitle: string;
  private cancelUrl: string = "account/membership-options";

  public identibasePlusAvailable: boolean = true;
  public identiAlertAvailable: boolean = true;
  public smartTagSubAvailable: boolean = true;

  @ViewChild('smartTagPicker') private smartTagPicker?: ElementRef<HTMLElement>;

  constructor(
    private _logService: LogService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private configService: ConfigService,
    public membershipOptionsService: MemberShipOptionsService,
    private _gtmService: GoogleTagManagerService,
    private featureFlagService: FeatureFlagsService,
    private registrationFlowService: RegistrationFlowService,
    private paymentConfigService: PaymentConfigService,
    private checkoutService: CheckoutService,
    private smartTagService: SmartTagService,
    private petService: PetService,
    private modalService: NgbModal,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.user = this._activatedRoute.snapshot.data.user as User;

    this.page = this.configService.getPage('plans');
    this.plan1Name = this.configService.getTextFromKey('1-plan-name');
    this.plan2Name = this.configService.getTextFromKey('2-plan-name');
    this.plan3Name = this.configService.getTextFromKey('3-plan-name');

    this.plan1Route = this.configService.getTextFromKey('1-plan-route');
    this.plan2Route = this.configService.getTextFromKey('2-plan-route');
    this.plan3Route = this.configService.getTextFromKey('3-plan-route');

    // this.membershipSelectionVet4PetTitle = this.configService.getTextFromKey('membership-selection-vet-4-pets-title');
    this.membershipSelectionSubTitle = this.configService.getTextFromKey('membership-selection-sub-title');
    this.membershipSelectionSubTitleButtonText = this.configService.getTextFromKey('membership-selection-sub-title-button-text');

    this.plan1YearlyPrice = this.configService.getTextFromKey('1-plan-yearly-price');
    this.plan2YearlyPrice = this.configService.getTextFromKey('2-plan-yearly-price');
    this.plan3YearlyPrice = this.configService.getTextFromKey('3-plan-yearly-price');
    this.plan3YearlyPriceOriginal = this.configService.getTextFromKey('3-plan-yearly-price-original');
    this.tagUnitPrice = this.configService.getNumberFromKey('3-plan-tag-unit-price');

    this.plan1Benefits = this.configService.getListContentFromKey('1-plan-benefits');
    this.plan2Benefits = this.configService.getListContentFromKey('2-plan-benefits');
    this.plan3Benefits = this.configService.getListContentFromKey('3-plan-benefits');

    this.plan1YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.BasicPlus) ?? this.plan1YearlyPrice;
    this.plan2YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.Premium) ?? this.plan2YearlyPrice;
    // Null when the backend offers Alert yearly-only, which skips the billing-interval modal.
    this.plan2MonthlyPrice = this.paymentConfigService.getMonthlyDisplayPrice(
      ProductType.Premium,
      this.configService.getTextFromKey('2-plan-monthly-price')
    );
    this.plan3YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.Locator) ?? this.plan3YearlyPrice;

    this.isIdentiSmartTagPlanAvailable = this.featureFlagService.isEnabled('isIdentiSmartTagPlanAvailable');
    this.isRegistrationPaymentConfigEnabled = this.featureFlagService.isEnabled('isRegistrationPaymentConfigEnabled');

    this.userAlreadyHasASubscription = this.membershipOptionsService.doesUserHaveMembership(
      this.user
    );

    if (this.userAlreadyHasASubscription) {
      this.currentMembershipName = this.membershipOptionsService.getUserMembershipName(this.user);
    }
  }

  ngOnInit(): void {
    if (this.user) {
      this.userCurrentProductType = this.user.productType;
      this.petName = this.petService.createPetsListString(this.user.pets);
      this.identibasePlusAvailable = this.membershipOptionsService.identiBasePlusAvailable(this.user);
      this.identiAlertAvailable = this.membershipOptionsService.identiAlertAvailable(this.user);
      this.smartTagSubAvailable = this.membershipOptionsService.smartTagSubAvailable(this.user);
    }
    this.basePlanSubtitle = this.withPetName(this.page.basePlan.subtitle);
    this.alertPlanSubtitle = this.withPetName(this.page.alertPlan.subtitle);
    this.locatorPlanSubtitle = this.withPetName(this.page.locatorPlan.subtitle);

    // Payment config (cards + skip button), pre-warmed by the resolver and read from cache.
    if (this.isBrowser && this.isRegistrationPaymentConfigEnabled) {
      const paymentConfig = this.paymentConfigService.config;
      if (paymentConfig) {
        this.applyRegistrationPaymentConfig(paymentConfig);
      }
    }

    // Warm the steps cache so getNextStepRoute() can resolve the next step.
    this.registrationFlowService.loadSteps().subscribe(() => {
      this.showProgressStepper = !this.registrationFlowService.isStepCompleted(RegistrationFlowStep.MembershipOptions);
    });

    if (this.isBrowser && this.isIdentiSmartTagPlanAvailable) {
      this.loadSmartTagLimit();
    }
  }

  // Users mid-registration may have no pet yet, so the placeholder needs a fallback.
  private withPetName(text: string): string {
    return text?.replace('{{petName}}', this.petName || 'your pet');
  }

  public selectBase(): void {
    if (!this.identibasePlusAvailable) return;
    this.closeAlert();
    this.showSmartTagPicker = false;
    this.checkoutService
      .checkout([environment.stripePlans.identibasePlus], {
        isUpgrade: this.userCurrentProductType === ProductType.Basic,
        cancelUrl: this.cancelUrl
      })
      .subscribe({
        error: () => this.showGenericError(),
      });
  }

  public selectAlert(): void {
    if (!this.identiAlertAvailable) return;
    this.closeAlert();
    this.showSmartTagPicker = false;
    // Without a backend monthly price the modal would hold a single option, so buy annual directly.
    if (!this.plan2MonthlyPrice) {
      this.checkoutService
        .checkout([environment.stripePlans.identiAlertAnnual], {
          isUpgrade: this.userCurrentProductType === ProductType.Basic,
          cancelUrl: this.cancelUrl
        })
        .subscribe({
          error: () => this.showGenericError(),
        });
      return;
    }
    const modalRef = this.modalService.open(IdentialertSubSelectionModalComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.isUpgrade = this.userCurrentProductType === ProductType.Basic;
    modalRef.componentInstance.cancelUrl = this.cancelUrl;
  }

  /** Shows the tag configurator. The user picks the tags first, then the picker's CTA starts the checkout. */
  public selectLocator(): void {
    if (!this.smartTagSubAvailable) return;
    this.closeAlert();

    this.showSmartTagPicker = true;
    this.scrollToSmartTagPicker();

    // Without the allowance the configurator stays locked.
    if (this.limitFailed) this.showGenericError();
  }

  private showGenericError(): void {
    this.alert = { type: 'danger', message: errorMessagesMap.generic_error_message };
  }

  /** Buys the Locator plan on its own, without any tag. */
  public checkoutLocatorOnly(): void {
    if (!this.smartTagSubAvailable) return;
    this.checkoutService.checkout([environment.stripePlans.smartTagSubscription], { cancelUrl: this.cancelUrl }).subscribe({
      error: () => this.showGenericError(),
    });
  }

  /** Every tag is covered by the plan, so the session only pays for the subscription. */
  public checkoutLocatorWithFreeTags(tags: SmartTagOrderItem[]): void {
    this.checkoutLocatorWithTags([environment.stripePlans.smartTagSubscription], tags);
  }

  /** Some tags are extra. The tag price goes in once, the backend counts the paid ones. */
  public checkoutLocatorWithPaidTags(tags: SmartTagOrderItem[]): void {
    this.checkoutLocatorWithTags(
      [environment.stripePlans.smartTagSubscription, environment.stripePlans.smartTagPhysicalProduct],
      tags
    );
  }

  private checkoutLocatorWithTags(pricingPlanIds: string[], tags: SmartTagOrderItem[]): void {
    if (!this.smartTagSubAvailable) return;
    this.checkoutService.checkout(pricingPlanIds, { smartTag: { tags }, cancelUrl: this.cancelUrl }).subscribe({
      error: () => this.showGenericError(),
    });
  }

  // Warms the allowance while the user reads the cards. It must never fall back to zero
  // free tags: that would charge the keeper for tags the plan already covers.
  private loadSmartTagLimit(): void {
    this.smartTagService.getSmartTagLimit().subscribe({
      next: (limit) => this.tagPricing = limit,
      error: (err) => {
        this.limitFailed = true;
        this._logService.error(errorMessagesMap.smart_tag_limit_error, err);
        // The keeper may already be looking at the locked picker.
        if (this.showSmartTagPicker) this.showGenericError();
      },
    });
  }

  private scrollToSmartTagPicker(): void {
    if (!this.isBrowser) return;
    setTimeout(() => this.smartTagPicker?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  public navigateToProductPage(productPageRoute: string): void {
    this._router.navigate([productPageRoute]);
  }

  private pushGTMTag(user: User): void {
    if (!environment.googleTagManagerId || !environment.conversionSendToParam) return;

    const productName = this.membershipOptionsService.getUserMembershipAnalyticsName(user);

    this._gtmService.pushTag({
      event: 'conversion',
      send_to: environment.conversionSendToParam,
      product_name: productName,
      environment: environment.name
    }).catch(() => {});
  }

  /**
   * Applies the /api/registration/me/payment response.
   * isPaymentSkippable decides whether the keep basic account button shows, and
   * availableProductIds overrides the per-card availability booleans below.
   */
  private applyRegistrationPaymentConfig(info: RegistrationPaymentInfo): void {
    this.showBasicAccountButton = info.isPaymentSkippable;
    this.showVetGroupMessage = info.context?.vetGroup === VET_GROUP_V4P;
    this.availableProductIds = this.paymentConfigService.getAvailableProductIds();
    this.applyAvailableProductIds();
  }

  private applyAvailableProductIds(): void {
    const availability = this.paymentConfigService.getProductAvailability(this.availableProductIds);
    if (!availability) return;
    this.identibasePlusAvailable = availability.basicPlus;
    this.identiAlertAvailable = availability.premium;
    this.smartTagSubAvailable = availability.smartTag;
  }

  private getNextStepRoute(): string {
    return this.registrationFlowService.getNextRoute(RegistrationFlowStep.MembershipOptions) ?? '/welcome-gift';
  }

  public continueWithCurrentMembership() {
    // Ignores repeated clicks while the step is being completed; the page is left afterwards
    if (this.loading) {
      return;
    }
    this.completeMembershipStep().subscribe(() => this._router.navigate([this.getNextStepRoute()]));
  }

  public completeMembershipSelection() {
    if (!this.loading) {
      // Keepers back here after registration, for example adding a pet, were already counted as a
      // conversion
      const isFirstSelection = !this.registrationFlowService.isStepCompleted(RegistrationFlowStep.MembershipOptions);
      this.completeMembershipStep()
        .subscribe(
          () => {
            this.alert = {
              type: 'success',
              message:
                "Your account information was updated. You'll get redirected in a few seconds!",
            };
            if (isFirstSelection) {
              this.pushGTMTag(this.user);
            }
            setTimeout(() => {
              this._router.navigate([this.getNextStepRoute()]);
            }, 1000);
            return;
          },
          (err) => {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
            this._logService.error(
              'An error has occurred during membership selection save. Please try again later. ',
              err
            );
          }
        )
        .add(() => {
          this.loading = false;
        });
    }
  }

  public closeAlert() {
    this.alert = null;
  }

  // Completes the step on the backend, unless it already is, for example for keepers adding a pet.
  // Paid plans don't come through here: the backend completes the step after a successful payment.
  private completeMembershipStep(): Observable<void> {
    this.closeAlert();
    this.loading = true;
    return this.registrationFlowService.completeStep(RegistrationFlowStep.MembershipOptions);
  }
}
